import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Palette, Download, Eye } from 'lucide-react';
import XpertLogo from '../XpertLogo';
import { printElement } from '../../lib/importExport';
import { toast } from 'sonner';

const defaultTemplate = {
  primary: '#8BC53F', accent: '#F59E0B', companyName: 'Xpertpests Solutions',
  address: 'P.O. Box 4462\nCarson, CA 90745', phone: '562-215-8002 · 310-629-9049', email: 'info@xpertpestssolutions.com',
  taxId: 'EIN —', license: 'CA Branch 2 PCO',
  footerNote: 'Real Results, Forget the Rest! Thank you for choosing Xpertpests Solutions.',
  terms: 'Net 30. Late fee of 1.5% per month on past-due balances.',
};

export default function TemplatesPage() {
  const [t, setT] = useState(() => {
    const stored = localStorage.getItem('xp_template');
    return stored ? JSON.parse(stored) : defaultTemplate;
  });
  const [type, setType] = useState('invoice');
  const update = (k, v) => { const next = {...t, [k]: v}; setT(next); localStorage.setItem('xp_template', JSON.stringify(next)); };
  const onPrint = () => { printElement('#template-preview'); toast.success('Opening print preview…'); };

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Customizable Templates</h1><p className="text-slate-500 mt-1">Brand your invoices, estimates &amp; statements.</p></div>
        <Button onClick={onPrint} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5"><Download className="w-4 h-4"/>Print / PDF</Button>
      </div>
      <div className="grid lg:grid-cols-[400px_1fr] gap-5">
        <Card className="p-5 border-slate-200 space-y-4 h-fit">
          <div className="flex items-center gap-2"><Palette className="w-5 h-5 text-[#8BC53F]"/><h3 className="font-bold text-slate-900">Customize</h3></div>
          <div><Label className="text-xs">Company Name</Label><Input value={t.companyName} onChange={e=>update('companyName',e.target.value)} className="mt-1.5 h-9"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Primary Color</Label><div className="flex gap-2 mt-1.5"><input type="color" value={t.primary} onChange={e=>update('primary',e.target.value)} className="h-9 w-12 rounded border border-slate-200 cursor-pointer"/><Input value={t.primary} onChange={e=>update('primary',e.target.value)} className="h-9"/></div></div>
            <div><Label className="text-xs">Accent Color</Label><div className="flex gap-2 mt-1.5"><input type="color" value={t.accent} onChange={e=>update('accent',e.target.value)} className="h-9 w-12 rounded border border-slate-200 cursor-pointer"/><Input value={t.accent} onChange={e=>update('accent',e.target.value)} className="h-9"/></div></div>
          </div>
          <div><Label className="text-xs">Address (multiline)</Label><textarea value={t.address} onChange={e=>update('address',e.target.value)} rows={2} className="mt-1.5 w-full text-sm border border-slate-200 rounded-md p-2"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Phone</Label><Input value={t.phone} onChange={e=>update('phone',e.target.value)} className="mt-1.5 h-9"/></div>
            <div><Label className="text-xs">Email</Label><Input value={t.email} onChange={e=>update('email',e.target.value)} className="mt-1.5 h-9"/></div>
            <div><Label className="text-xs">Tax ID</Label><Input value={t.taxId} onChange={e=>update('taxId',e.target.value)} className="mt-1.5 h-9"/></div>
            <div><Label className="text-xs">License #</Label><Input value={t.license} onChange={e=>update('license',e.target.value)} className="mt-1.5 h-9"/></div>
          </div>
          <div><Label className="text-xs">Footer Note</Label><Input value={t.footerNote} onChange={e=>update('footerNote',e.target.value)} className="mt-1.5 h-9"/></div>
          <div><Label className="text-xs">Terms</Label><Input value={t.terms} onChange={e=>update('terms',e.target.value)} className="mt-1.5 h-9"/></div>
        </Card>
        <div>
          <Tabs value={type} onValueChange={setType}>
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="invoice">Invoice</TabsTrigger>
              <TabsTrigger value="estimate">Estimate</TabsTrigger>
              <TabsTrigger value="statement">Statement</TabsTrigger>
            </TabsList>
            <TabsContent value={type} className="mt-4">
              <Card className="p-0 border-slate-200 overflow-hidden">
                <TemplatePreview t={t} type={type}/>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function TemplatePreview({ t, type }) {
  const title = { invoice: 'INVOICE', estimate: 'ESTIMATE', statement: 'STATEMENT' }[type];
  const docNum = { invoice: 'INV-1006', estimate: 'EST-2003', statement: 'STMT-7-2025' }[type];
  const items = type === 'statement' ? [
    { d: '2025-06-22', desc: 'Invoice INV-1001 – Commercial IPM', amt: 515 },
    { d: '2025-06-28', desc: 'Invoice INV-1002 – Multi-Unit', amt: 1788 },
    { d: '2025-07-01', desc: 'Invoice INV-1003 – Quarterly', amt: 149 },
    { d: '2025-07-04', desc: 'Payment received', amt: -149 },
  ] : [
    { d: 'Quarterly Pest Treatment', qty: 1, rate: 149, amt: 149 },
    { d: 'Termite Inspection', qty: 1, rate: 195, amt: 195 },
    { d: 'Bait Station Replacement (8)', qty: 8, rate: 18, amt: 144 },
  ];
  const total = items.reduce((a,i)=>a + (i.amt || 0), 0);
  return (
    <div id="template-preview" className="bg-white p-10 text-sm">
      <div className="flex justify-between items-start pb-6 border-b-4" style={{borderColor:t.primary}}>
        <div className="flex items-start gap-3">
          <XpertLogo size={56}/>
          <div>
            <div className="text-2xl font-extrabold" style={{color:t.primary}}>{t.companyName}</div>
            <div className="text-xs text-slate-600 whitespace-pre-line mt-1">{t.address}</div>
            <div className="text-xs text-slate-600 mt-1">{t.phone} · {t.email}</div>
            <div className="text-[10px] text-slate-500 mt-1">{t.taxId} · {t.license}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-extrabold tracking-tight" style={{color:t.primary}}>{title}</div>
          <div className="mt-2 inline-block px-3 py-1 rounded text-xs font-bold text-white" style={{backgroundColor:t.accent}}>{docNum}</div>
          <div className="text-xs text-slate-500 mt-2">Issued: {new Date().toLocaleDateString()}</div>
          {type !== 'statement' && <div className="text-xs text-slate-500">Due: {new Date(Date.now()+30*86400000).toLocaleDateString()}</div>}
        </div>
      </div>
      <div className="mt-6 flex justify-between">
        <div>
          <div className="text-[10px] font-bold tracking-widest text-slate-500">{type==='statement'?'TO':'BILL TO'}</div>
          <div className="font-bold text-slate-900 mt-1">Brewster Office Complex</div>
          <div className="text-sm text-slate-600">P.O. Box 4462</div>
          <div className="text-sm text-slate-600">Carson, CA 90745</div>
          <div className="text-sm text-slate-600">ap@brewsterco.com</div>
        </div>
        {type !== 'statement' && (
          <div className="text-right">
            <div className="text-[10px] font-bold tracking-widest text-slate-500">SERVICE</div>
            <div className="font-bold text-slate-900 mt-1">Commercial IPM</div>
            <div className="text-sm text-slate-600">Service date: 2025-07-15</div>
            <div className="text-sm text-slate-600">Technician: Mike Rodriguez</div>
          </div>
        )}
      </div>
      <table className="w-full mt-7">
        <thead><tr style={{backgroundColor: t.primary + '12'}}>
          {type==='statement' ? <><th className="text-left py-2 px-3 text-[11px] font-bold uppercase tracking-wider text-slate-600">Date</th><th className="text-left py-2 px-3 text-[11px] font-bold uppercase tracking-wider text-slate-600">Description</th><th className="text-right py-2 px-3 text-[11px] font-bold uppercase tracking-wider text-slate-600">Amount</th></>
          : <><th className="text-left py-2 px-3 text-[11px] font-bold uppercase tracking-wider text-slate-600">Description</th><th className="text-center py-2 px-3 text-[11px] font-bold uppercase tracking-wider text-slate-600">Qty</th><th className="text-right py-2 px-3 text-[11px] font-bold uppercase tracking-wider text-slate-600">Rate</th><th className="text-right py-2 px-3 text-[11px] font-bold uppercase tracking-wider text-slate-600">Amount</th></>}
        </tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} className="border-b border-slate-100">
              {type==='statement' ? <><td className="py-2.5 px-3 text-slate-600">{it.d}</td><td className="py-2.5 px-3 text-slate-900">{it.desc}</td><td className="py-2.5 px-3 text-right tabular-nums font-semibold" style={{color: it.amt<0?'#8BC53F':'inherit'}}>${it.amt.toFixed(2)}</td></>
              : <><td className="py-2.5 px-3 text-slate-900">{it.d}</td><td className="py-2.5 px-3 text-center text-slate-600">{it.qty}</td><td className="py-2.5 px-3 text-right text-slate-600 tabular-nums">${it.rate.toFixed(2)}</td><td className="py-2.5 px-3 text-right font-semibold tabular-nums">${it.amt.toFixed(2)}</td></>}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-6 flex justify-end">
        <div className="w-64">
          {type !== 'statement' && <><div className="flex justify-between py-1 text-slate-600"><span>Subtotal</span><span className="tabular-nums">${total.toFixed(2)}</span></div><div className="flex justify-between py-1 text-slate-600"><span>Tax (8.25%)</span><span className="tabular-nums">${(total*0.0825).toFixed(2)}</span></div></>}
          <div className="flex justify-between py-2 mt-2 border-t-2 text-lg font-extrabold" style={{color: t.primary, borderColor: t.primary}}><span>{type==='statement'?'BALANCE DUE':'TOTAL'}</span><span className="tabular-nums">${type==='statement' ? total.toFixed(2) : (total*1.0825).toFixed(2)}</span></div>
        </div>
      </div>
      <div className="mt-10 pt-5 border-t border-slate-200 text-center text-xs text-slate-500">
        <div className="font-medium text-slate-700">{t.footerNote}</div>
        <div className="mt-1">{t.terms}</div>
      </div>
    </div>
  );
}
